import React, { useEffect, useState } from "react";
import { ShieldAlert, FileWarning, Wrench, TrendingDown, ChevronDown } from "lucide-react";
import { api } from "../../services/api";
import IGLDecisionCard from "../../components/igl/IGLDecisionCard";

function Mark({ shape, color }) { return <span className={`type-mark ${shape}`} style={{ "--mk": color }} />; }

const CATS = [
  { id: "safety", label: "Immediate Safety Review", shape: "square", color: "#dc2626", icon: ShieldAlert },
  { id: "compliance", label: "Compliance Anomaly", shape: "circle", color: "#d97706", icon: FileWarning },
  { id: "maintenance", label: "Maintenance Correlation", shape: "triangle", color: "#4f46e5", icon: Wrench },
  { id: "demand", label: "Demand Shift", shape: "diamond", color: "#0891b2", icon: TrendingDown },
];

export default function IGLDecisions() {
  const [summary, setSummary] = useState(null);
  const [data, setData] = useState(null);
  const [showBelow, setShowBelow] = useState(false);

  useEffect(() => {
    api.iglSummary().then(setSummary);
    api.iglDecisions().then(setData);
  }, []);

  if (!summary || !data) return <div className="loading-page">Correlating IGL safety & compliance signals…</div>;

  return (
    <div className="vans-page">
      <section className="vans-hero igl-hero">
        <div className="vans-hero-left">
          <div className="vans-eyebrow">Indraprastha Gas Limited · Safety & Compliance Command Centre · {summary.today}</div>
          <h1 className="vans-title">Decisions to Take Today</h1>
          <p className="vans-sub">
            DotIQ correlated independent signals — station telemetry, maintenance work orders, incidents, customer
            complaints and hydro-test compliance — into {summary.top_count} prioritised, evidence-backed decisions
            across {summary.stations_monitored} stations. A station becomes high-priority only when multiple
            independent signals combine into a meaningful risk pattern.
          </p>
          <div className="vans-legend">
            {CATS.map((c) => <span key={c.id} className="vl-item"><Mark shape={c.shape} color={c.color} /> {c.label}</span>)}
          </div>
        </div>
        <div className="vans-metrics">
          <div className="vmetric hero igl">
            <ShieldAlert size={16} />
            <div className="vm-val">{summary.critical_count}</div>
            <div className="vm-lbl">Immediate safety review{summary.critical_count !== 1 ? "s" : ""}</div>
          </div>
          <div className="vmetric"><div className="vm-val">{summary.top_count}</div><div className="vm-lbl">Ranked decisions</div></div>
          <div className="vmetric"><div className="vm-val">{summary.stations_flagged}</div><div className="vm-lbl">Stations flagged</div></div>
          <div className="vmetric"><div className="vm-val">{summary.systems_connected}</div><div className="vm-lbl">Systems connected</div></div>
        </div>
      </section>

      <div className="decision-list">
        {data.top.map((d) => <IGLDecisionCard key={d.id} d={d} defaultOpen={d.rank === 1} />)}
      </div>

      <section className="below-section">
        <button className="below-toggle" onClick={() => setShowBelow((v) => !v)}>
          <ChevronDown size={16} className={`chev ${showBelow ? "open" : ""}`} />
          Below today's cutoff — {data.below.length} more monitored (low corroboration)
        </button>
        {showBelow && (
          <div className="below-grid">
            {data.below.map((d) => (
              <div key={d.id} className="below-mini">
                <div className="bm-top">
                  <span className="type-pill" style={{ background: d.category_meta.soft, color: d.category_meta.text }}>
                    <Mark shape={d.category_meta.shape} color={d.category_meta.color} /> {d.category_label}
                  </span>
                  <span className="bm-conf">score {d.score}</span>
                </div>
                <div className="bm-title">{d.entity_name}</div>
                <div className="bm-head">{d.why_summary}</div>
              </div>
            ))}
          </div>
        )}
      </section>

      <footer className="vans-foot">DotIQ × IGL — POC3 sandbox · synthetic/anonymized data · no automated equipment control · human approval required</footer>
    </div>
  );
}
